import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const psychologyUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Social Psychology and Personality",
  examWeight: "15–25%",
  focus: "How we perceive, interpret, and interact with others; how personality is formed and expressed.",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Attribution Theory and Person Perception",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Attribution Theory</h3>
            <KeyTerm 
              term="Attribution Theory" 
              definition="The process by which we explain the causes of our own behavior and the behavior of others. We attribute behavior to either the situation or the disposition."
            />
            <div className="space-y-3 mt-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Dispositional Attribution (Internal)</h4>
                <p className="text-sm text-slate-700 mt-1">Attributing behavior to the person's internal traits, personality, or intelligence (e.g., "He cut me off because he is a jerk").</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Situational Attribution (External)</h4>
                <p className="text-sm text-slate-700 mt-1">Attributing behavior to the environment or circumstances (e.g., "He cut me off because he is rushing to the hospital").</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Attributional Biases</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Humans are prone to errors when explaining behavior.</p>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Fundamental Attribution Error (FAE)</h4>
                <p className="text-sm text-slate-700 mt-2">The tendency to <strong>overestimate dispositional factors</strong> and <strong>underestimate situational factors</strong> when explaining <em>others'</em> behavior.</p>
                <p className="text-xs text-red-700 mt-2"><strong>Example:</strong> You see a student sleeping in class and assume they are lazy (disposition), ignoring that they might work a night job to support their family (situation).</p>
                <p className="text-xs text-red-700 mt-1"><strong>Cultural Note:</strong> FAE is more common in Individualistic cultures (Western) than Collectivist cultures (Eastern).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Actor-Observer Bias</h4>
                <p className="text-sm text-slate-700 mt-2">The tendency to attribute <strong>our own</strong> behavior to situational causes (because we know our circumstances) but <strong>others'</strong> behavior to dispositional causes.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Self-Serving Bias</h4>
                <p className="text-sm text-slate-700 mt-2">The tendency to attribute our <strong>successes</strong> to internal characteristics (disposition) and our <strong>failures</strong> to environmental factors (situation) to protect our self-esteem.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-2">
                  <li><strong>Success:</strong> "I aced the test because I'm smart."</li>
                  <li><strong>Failure:</strong> "I failed the test because the teacher hates me."</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Explanatory Style</h3>
            <p className="text-slate-700 leading-relaxed mb-4">How people explain the good and bad events in their lives.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Optimistic Explanatory Style</h4>
                <p className="text-sm text-slate-700 mt-1">Attributes setbacks to temporary, specific, and external causes.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Pessimistic Explanatory Style</h4>
                <p className="text-sm text-slate-700 mt-1">Attributes setbacks to permanent, universal, and internal causes (linked to depression).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Person Perception & Attraction</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Mere Exposure Effect" 
                definition="The phenomenon where repeated exposure to a novel stimulus increases our liking of it. Familiarity breeds fondness."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Self-Fulfilling Prophecy</h4>
                <p className="text-sm text-slate-700 mt-2">Expectations about an individual cause that person to behave in ways that confirm the original expectation.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Rosenthal & Jacobson (Pygmalion Effect):</strong> Teachers were told specific students were "bloomers." Those students actually achieved more because the teachers treated them differently based on the expectation.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Social Comparison Theory</h4>
                <p className="text-sm text-slate-700 mt-2">We evaluate ourselves by comparing ourselves to others.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-2">
                  <li><strong>Upward Comparison:</strong> Comparing to someone "better" (can lower self-esteem or inspire).</li>
                  <li><strong>Downward Comparison:</strong> Comparing to someone "worse" (boosts self-esteem).</li>
                </ul>
              </div>
              <KeyTerm 
                term="Relative Deprivation" 
                definition="The perception that one is worse off relative to those with whom one compares oneself."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Attitude Formation and Attitude Change",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Attitudes</h3>
            <KeyTerm 
              term="Attitudes" 
              definition="Evaluation of a person, object, or idea."
            />
            <div className="space-y-3 mt-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">The ABC Model</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>A</strong>ffective (Emotional feeling)</li>
                  <li><strong>B</strong>ehavioral (Action taken)</li>
                  <li><strong>C</strong>ognitive (Belief/Thought)</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Implicit vs. Explicit Attitudes</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Explicit:</strong> Conscious, reportable attitudes.</li>
                  <li><strong>Implicit:</strong> Unconscious attitudes that influence behavior without our awareness (often tested via the IAT - Implicit Association Test).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Stereotypes, Prejudice, and Discrimination</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Stereotype (Cognitive)" 
                definition="A generalized belief (schema) about a group of people. Can be positive or negative."
              />
              <KeyTerm 
                term="Prejudice (Affective)" 
                definition="An unjustifiable negative attitude toward a group and its members."
              />
              <KeyTerm 
                term="Discrimination (Behavioral)" 
                definition="Unjustifiable negative behavior toward a group and its members."
              />
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Causes of Bias</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Just-World Phenomenon:</strong> The belief that the world is fair, so people get what they deserve (leads to victim-blaming).</li>
                  <li><strong>In-Group Bias:</strong> Tendency to favor our own group.</li>
                  <li><strong>Out-Group Homogeneity Bias:</strong> The tendency to view members of out-groups as "all the same," while viewing our in-group as diverse.</li>
                  <li><strong>Ethnocentrism:</strong> Believing one's own culture is superior to others.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Attitude Change & Persuasion</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Cognitive Dissonance (Leon Festinger)</h4>
                <p className="text-sm text-slate-700 mt-2">The mental discomfort (tension) experienced when two thoughts are inconsistent, or when behavior conflicts with attitudes.</p>
                <p className="text-xs text-red-700 mt-2"><strong>Resolution:</strong> To reduce tension, we usually change our <strong>attitude</strong> to match our behavior (since we cannot take back the behavior).</p>
              </div>
              <KeyTerm 
                term="Belief Perseverance" 
                definition="Clinging to one's initial conceptions after the basis on which they were formed has been discredited."
              />
              <KeyTerm 
                term="Confirmation Bias" 
                definition="Seeking evidence that supports our existing beliefs and ignoring contradictory evidence."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Elaboration Likelihood Model (Routes to Persuasion)</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Central Route</h4>
                <p className="text-sm text-slate-700 mt-2">Persuasion based on logic, facts, and the content of the message.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Result:</strong> More durable, long-lasting attitude change.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Peripheral Route</h4>
                <p className="text-sm text-slate-700 mt-2">Persuasion based on incidental cues, such as the speaker's attractiveness, celebrity status, or emotional appeals.</p>
                <p className="text-xs text-orange-700 mt-2"><strong>Result:</strong> Temporary, superficial change.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Compliance Strategies</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Foot-in-the-Door Phenomenon" 
                definition="Agreeing to a small request increases the likelihood of agreeing to a larger request later."
              />
              <KeyTerm 
                term="Door-in-the-Face Phenomenon" 
                definition="Refusing a large request increases the likelihood of agreeing to a smaller, more reasonable request later."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Psychology of Social Situations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Social Influence</h3>
            <KeyTerm 
              term="Social Norms" 
              definition="Understood rules for accepted and expected behavior."
            />
            <div className="space-y-3 mt-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Conformity (Solomon Asch)</h4>
                <p className="text-sm text-slate-700 mt-2">Adjusting behavior or thinking to coincide with a group standard.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Asch's Line Study:</strong> Participants identified line lengths. When confederates (actors) gave the wrong answer, 1/3 of participants conformed and gave the wrong answer too.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-2">
                  <li><strong>Normative Social Influence:</strong> Conforming to gain approval or avoid rejection.</li>
                  <li><strong>Informational Social Influence:</strong> Conforming because we believe others are correct/have more information.</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Obedience (Stanley Milgram)</h4>
                <p className="text-sm text-slate-700 mt-2">Complying with direct commands from an authority figure.</p>
                <p className="text-xs text-red-700 mt-2"><strong>Milgram's Shock Experiment:</strong> Participants (teachers) were ordered to deliver painful shocks to a learner (confederate) for wrong answers. ~65% delivered the maximum (lethal) voltage.</p>
                <p className="text-xs text-red-700 mt-1"><strong>Factors Increasing Obedience:</strong> Authority figure nearby, victim depersonalized/far away, no role models for defiance.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Group Dynamics</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Social Facilitation" 
                definition="Improved performance on simple or well-learned tasks in the presence of others. (Note: Complex tasks may suffer—Social Impairment)."
              />
              <KeyTerm 
                term="Social Loafing" 
                definition="The tendency for people in a group to exert less effort when pooling their efforts toward a common goal than when individually accountable."
              />
              <KeyTerm 
                term="Deindividuation" 
                definition="The loss of self-awareness and self-restraint occurring in group situations that foster arousal and anonymity (e.g., riots, internet trolls)."
              />
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Group Polarization</h4>
                <p className="text-sm text-slate-700 mt-2">The enhancement of a group's prevailing inclinations through discussion within the group (if a group is slightly racist, they become <em>more</em> racist after discussing; if a group is philanthropic, they become <em>more</em> philanthropic).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Groupthink (Janis)</h4>
                <p className="text-sm text-slate-700 mt-2">The mode of thinking that occurs when the desire for harmony in a decision-making group overrides a realistic appraisal of alternatives (leads to bad decisions).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Prosocial Behavior (Helping)</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Altruism" 
                definition="Unselfish regard for the welfare of others."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Bystander Effect (Darley & Latané)</h4>
                <p className="text-sm text-slate-700 mt-2">The tendency for any given bystander to be less likely to give aid if other bystanders are present.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Diffusion of Responsibility:</strong> We assume someone else will handle it.</p>
              </div>
              <KeyTerm 
                term="Social Exchange Theory" 
                definition="We help only if the benefits outweigh the costs."
              />
              <KeyTerm 
                term="Reciprocity Norm" 
                definition="The expectation that we should help those who have helped us."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Conflict and Peacemaking</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Social Trap (Prisoner's Dilemma)" 
                definition="A situation in which conflicting parties become caught in mutually destructive behavior by pursuing their self-interest rather than the good of the group."
              />
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Superordinate Goals (Muzafer Sherif - Robbers Cave Experiment)</h4>
                <p className="text-sm text-slate-700 mt-2">Shared goals that override differences among people and require their cooperation. Used to reduce conflict between groups.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Psychodynamic and Humanistic Theories of Personality",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Psychodynamic Perspective (Sigmund Freud)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Focuses on the <strong>unconscious mind</strong> and childhood experiences.</p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Structure of Personality</h4>
                <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700 mt-2">
                  <li><strong>Id:</strong> Unconscious; operates on the <strong>Pleasure Principle</strong> (immediate gratification); repository of basic drives (sex/aggression).</li>
                  <li><strong>Ego:</strong> Mostly conscious; operates on the <strong>Reality Principle</strong>; mediates between Id and Superego.</li>
                  <li><strong>Superego:</strong> Moral compass/conscience; strives for perfection.</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Defense Mechanisms</h4>
                <p className="text-sm text-slate-700 mt-2">The Ego's protective methods of reducing anxiety by unconsciously distorting reality.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-2">
                  <li><strong>Repression:</strong> Pushing anxiety-arousing thoughts into the unconscious (the basis of all other mechanisms).</li>
                  <li><strong>Regression:</strong> Retreating to an earlier stage of development (e.g., throwing a tantrum).</li>
                  <li><strong>Reaction Formation:</strong> Switching unacceptable impulses into their opposites (e.g., being mean to a crush).</li>
                  <li><strong>Projection:</strong> Disguising one's own threatening impulses by attributing them to others (e.g., a liar claiming everyone else lies).</li>
                  <li><strong>Rationalization:</strong> Self-justifying explanations.</li>
                  <li><strong>Displacement:</strong> Shifting aggressive impulses toward a less threatening victim (e.g., kicking the dog after a bad day at work).</li>
                  <li><strong>Sublimation:</strong> Channeling frustration toward a socially acceptable goal (e.g., working out).</li>
                  <li><strong>Denial:</strong> Refusing to believe painful realities.</li>
                </ul>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Assessment</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Projective Tests</strong> (e.g., Rorschach Inkblot, Thematic Apperception Test [TAT]) provide ambiguous stimuli designed to trigger projection of one's inner dynamics.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Humanistic Perspective (Carl Rogers & Abraham Maslow)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Focuses on human potential, free will, and self-growth.</p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Abraham Maslow</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Self-Actualization:</strong> The motivation to fulfill one's potential (top of the hierarchy of needs).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Carl Rogers</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Self-Concept:</strong> All our thoughts and feelings about ourselves ("Who am I?").</li>
                  <li><strong>Real vs. Ideal Self:</strong> Distress occurs when these do not match (incongruence).</li>
                  <li><strong>Conditions for Growth (AGE):</strong> <strong>A</strong>cceptance (<strong>Unconditional Positive Regard</strong>), <strong>G</strong>enuineness, and <strong>E</strong>mpathy.</li>
                  <li><strong>Unconditional Positive Regard:</strong> An attitude of total acceptance toward another person.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Social-Cognitive and Trait Theories of Personality",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Social-Cognitive Perspective (Albert Bandura)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Views behavior as influenced by the interaction between people's traits (including their thinking) and their social context.</p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Reciprocal Determinism</h4>
                <p className="text-sm text-slate-700 mt-2">The interacting influences of behavior, internal cognition (personal factors), and environment.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Example:</strong> A child hates school (cognition) → acts out in class (behavior) → teachers dislike him (environment) → reinforces hatred of school.</p>
              </div>
              <KeyTerm 
                term="Self-Efficacy" 
                definition="One's sense of competence and effectiveness in a specific task. (Note: Different from self-esteem, which is overall self-worth)."
              />
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Locus of Control (Julian Rotter)</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Internal Locus of Control:</strong> Belief that you control your own fate. (Correlated with better health/achievement).</li>
                  <li><strong>External Locus of Control:</strong> Belief that outside forces or chance determine your fate.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Trait Perspective</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Focuses on <strong>describing</strong> personality traits (stable characteristics) rather than explaining their origins.</p>
            <div className="space-y-3">
              <KeyTerm 
                term="Factor Analysis" 
                definition="A statistical procedure used to identify clusters of test items that tap basic components of intelligence or personality."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">The Big Five Factors (Costa & McCrae)</h4>
                <p className="text-sm text-slate-700 mt-2">The most widely accepted trait theory today. (<strong>OCEAN</strong> or <strong>CANOE</strong>)</p>
                <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>O</strong>penness (imaginative vs. practical)</li>
                  <li><strong>C</strong>onscientiousness (organized/careful vs. impulsive/careless)</li>
                  <li><strong>E</strong>xtraversion (sociable vs. reserved)</li>
                  <li><strong>A</strong>greeableness (trusting/helpful vs. suspicious/uncooperative)</li>
                  <li><strong>N</strong>euroticism (emotional stability vs. instability/anxiety)</li>
                </ol>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Assessment</h4>
                <p className="text-sm text-slate-700 mt-2">Personality Inventories (e.g., MMPI - Minnesota Multiphasic Personality Inventory). These are objective tests.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Motivation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Theories of Motivation</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Instinct Theory (Evolutionary)" 
                definition="Genetically predisposed behaviors (fixed patterns) drive us."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Drive-Reduction Theory</h4>
                <p className="text-sm text-slate-700 mt-2">Physiological need creates an aroused tension state (a drive) that motivates an organism to satisfy the need to return to <strong>Homeostasis</strong> (steady internal state).</p>
                <p className="text-xs text-blue-700 mt-2"><em>Need (Water) → Drive (Thirst) → Drive-reducing behavior (Drinking).</em></p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Arousal Theory (Optimal Arousal)</h4>
                <p className="text-sm text-slate-700 mt-2">We are motivated to maintain an optimal level of arousal. Boredom drives us to seek stimulation; stress drives us to seek calm.</p>
                <p className="text-xs text-purple-700 mt-2"><strong>Yerkes-Dodson Law:</strong> Performance increases with arousal only up to a point, beyond which performance decreases.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-2">
                  <li><em>Easy tasks</em> require <em>high</em> arousal.</li>
                  <li><em>Difficult tasks</em> require <em>low</em> arousal.</li>
                </ul>
              </div>
              <KeyTerm 
                term="Incentive Theory" 
                definition="We are pulled by incentives (positive or negative environmental stimuli). Focuses on Extrinsic Motivation."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Hunger Motivation</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Physiology</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Hypothalamus:</strong> Controls hunger.
                    <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-1">
                      <li><em>Lateral Hypothalamus:</em> Stimulates hunger (Go signal).</li>
                      <li><em>Ventromedial Hypothalamus:</em> Depresses hunger (Stop signal).</li>
                    </ul>
                  </li>
                  <li><strong>Hormones:</strong>
                    <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-1">
                      <li><strong>Ghrelin:</strong> Secreted by empty stomach; sends "I'm hungry" signals to the brain.</li>
                      <li><strong>Leptin:</strong> Protein hormone secreted by fat cells; causes brain to increase metabolism and decrease hunger.</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <KeyTerm 
                term="Psychology of Hunger" 
                definition="Influenced by memory of last meal, cultural preferences, and situational factors (eating more with friends)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Motivational Conflicts (Kurt Lewin)</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <KeyTerm term="Approach-Approach" definition="Choosing between two desirable outcomes (e.g., Pizza vs. Tacos)." />
              <KeyTerm term="Avoidance-Avoidance" definition="Choosing between two undesirable outcomes (e.g., Chore A vs. Chore B)." />
              <KeyTerm term="Approach-Avoidance" definition="One event has both attractive and unattractive features (e.g., Asking someone on a date—risk of rejection vs. potential romance)." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Intrinsic vs. Extrinsic Motivation</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Intrinsic</h4>
                <p className="text-sm text-slate-700 mt-1">Driven by interest or enjoyment of the task itself.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Extrinsic</h4>
                <p className="text-sm text-slate-700 mt-1">Driven by rewards or avoiding punishment.</p>
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mt-4">
              <h4 className="font-bold text-red-800">Overjustification Effect</h4>
              <p className="text-sm text-slate-700 mt-2">Adding an extrinsic reward to a task that was intrinsically motivating can decrease the intrinsic interest.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-7",
      title: "Topic 4.7: Emotion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Theories of Emotion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">How do we experience emotion? Is it body first, or mind first?</p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">James-Lange Theory</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Physiological arousal comes first</strong>, <em>then</em> the conscious experience of emotion.</p>
                <p className="text-xs text-blue-700 mt-2"><em>Sequence:</em> Threat → Heart pounds → "I feel afraid."</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Cannon-Bard Theory</h4>
                <p className="text-sm text-slate-700 mt-2">Physiological arousal and emotional experience occur <strong>simultaneously</strong> and independently.</p>
                <p className="text-xs text-purple-700 mt-2"><em>Sequence:</em> Threat → Heart pounds AND "I feel afraid" (at the same time).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Schachter-Singer Two-Factor Theory</h4>
                <p className="text-sm text-slate-700 mt-2">To experience emotion one must be (1) physically aroused and (2) <strong>cognitively label</strong> the arousal based on context.</p>
                <p className="text-xs text-green-700 mt-2"><em>Sequence:</em> Threat → Heart pounds → "This situation is dangerous" (Label) → "I feel afraid."</p>
                <p className="text-xs text-green-700 mt-1"><strong>Spillover Effect:</strong> Arousal from one event spills over to fuel emotion in the next.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Zajonc & LeDoux (High Road vs. Low Road)</h4>
                <p className="text-sm text-slate-700 mt-2">Some emotional responses (like fear) happen instantly, without conscious appraisal (<strong>Low Road</strong>: Thalamus → Amygdala), while others (like love) require interpretation (<strong>High Road</strong>: Thalamus → Cortex → Amygdala).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Expressing Emotion</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Facial Feedback Hypothesis</h4>
                <p className="text-sm text-slate-700 mt-2">The tendency of facial muscle states to trigger corresponding feelings such as fear, anger, or happiness. (Smiling makes you feel happier).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Universality of Emotion</h4>
                <p className="text-sm text-slate-700 mt-2">Paul Ekman found that facial expressions for basic emotions (happiness, surprise, fear, sadness, anger, disgust) are universal across cultures.</p>
              </div>
              <KeyTerm 
                term="Display Rules" 
                definition="Cultural rules that govern how and when emotions should be expressed (e.g., Individualist cultures express more visible emotion than Collectivist cultures)."
              />
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default psychologyUnit4Notes;